import { ChangeEvent, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { UploadSimple } from "@phosphor-icons/react"
import { PayloadError } from "relay-runtime"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { UserProvider } from "@/components/user-wrapper"
import { formatInServerTimeZone } from "@/lib/initial-time-zone"
import { useImportCsvCapacityPlanMutation$data } from "@/modules/admin/capacity/services/__generated__/useImportCsvCapacityPlanMutation.graphql"
import { useForecastFileUploadQuery } from "@/modules/admin/capacity/services/useForecastFileUploadQuery"
import { useImportCsvCapacityPlanMutation } from "@/modules/admin/capacity/services/useImportCsvCapacityPlanMutation"

const SUPPORT_FILE_TYPE = ".csv"

const ForecastFileUploadCard = () => {
  const { t } = useTranslation()
  const [uploadFailed, setUploadFailed] = useState(false)
  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const [setImportCsvCapacityPlanMutation, isImportCsvProcessing] = useImportCsvCapacityPlanMutation()
  const { forecastFileUpload } = useForecastFileUploadQuery()
  const isForecastFileUploaderAvailable = forecastFileUpload.uploadAt && forecastFileUpload.uploadBy

  const handleClick = () => {
    hiddenFileInput.current?.click()
  }

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    if (!input.files?.length) {
      return
    }
    const [uploadFile] = input.files
    if (!uploadFile.name.endsWith(SUPPORT_FILE_TYPE)) {
      setUploadFailed(true)
      return
    }
    setImportCsvCapacityPlanMutation(
      {
        file: uploadFile,
      },
      (response: useImportCsvCapacityPlanMutation$data, errors: PayloadError[] | null) => {
        const errorList = errors ? errors : response.importCsvCapacityPlan.errors
        if (errorList) {
          console.error("Error on the forecast file upload:", errorList)
          setUploadFailed(true)
        } else {
          setUploadFailed(false)
        }
      },
      (error) => {
        setUploadFailed(true)
        console.error("Error on the forecast file upload:", error)
      },
    )
  }

  return (
    <Card className="h-fit w-92.5">
      <CardHeader>
        <CardHeaderTitle data-testid="card-header-title-content" className="flex items-center gap-4">
          {t("admin.capacity.forecastFileUpload.title")}
        </CardHeaderTitle>
        <div className="flex justify-end">
          <Button variant="tertiary" isLoading={isImportCsvProcessing} className="p-2" onClick={handleClick}>
            <UploadSimple className="text-on-surface-subdued" />
            <input
              type="file"
              onChange={handleChange}
              ref={hiddenFileInput}
              className="hidden"
              accept={SUPPORT_FILE_TYPE}
            />
          </Button>
        </div>
      </CardHeader>
      {(uploadFailed || isForecastFileUploaderAvailable) && (
        <div className="grid items-center gap-4 p-4 pt-0">
          {uploadFailed && (
            <Banner
              data-testid="forecast-file-upload-failed"
              type="inline"
              variant="warning"
              className="flex items-center gap-1 rounded-md">
              {t("admin.capacity.uploadFailed")}
            </Banner>
          )}
          {isForecastFileUploaderAvailable && (
            <div className="flex items-center gap-2">
              <UserProvider
                userName={forecastFileUpload.uploadBy}
                render={(user) => (
                  <UserAvatarTooltip user={user} uniqueIdentifier="last-updated-forecast-file"></UserAvatarTooltip>
                )}
              />
              <div className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
                {`${t("admin.capacity.uploadedOn")} ${formatInServerTimeZone(
                  forecastFileUpload.uploadAt,
                  "dd.MM.yyyy HH:mm",
                )}`}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

export { ForecastFileUploadCard }
